import React, { useEffect, useMemo } from 'react'
import { Alert, Group, Select, Stack, Text, Textarea } from '@mantine/core'

import { BooleanSelect } from '@/components/common/BooleanSelect'
import { ConditionalScriptBlock } from '@/components/Event/CrForm/components/ConditionalScriptBlock'
import { CrTurnaroundSelect } from '@/components/Event/CrForm/components/CrTurnaroundSelect'
import { ScriptBlock } from '@/components/Event/CrForm/components/ScriptBlock'
import { StateScriptBlock } from '@/components/Event/CrForm/components/StateScriptBlock'
import { TimestampsControl } from '@/components/Event/CrForm/components/TimestampsControl'
import {
  CrFormHandle,
  CrFormStep,
  witnessStepKey,
} from '@/components/Event/CrForm/CrFormStep'
import {
  crReadWaiveToRecord,
  READ_WAIVE_OPTIONS,
} from '@/components/Event/CrForm/saFields'
import {
  getScriptValues,
  getStateRecordScript,
} from '@/components/Event/CrForm/stateRecordScripts'
import { Step } from '@/components/Event/CrForm/Step'
import { useRegisterStepFields } from '@/components/Event/CrForm/useStepField'
import { EventResultFields } from '@/components/Event/Feedback/EventResultFields'
import { useFormattedCurrentDateParts } from '@/components/Event/NewScript/hooks/useFormattedCurrentDateParts'
import {
  fixedReadWaiveAnswer,
  isReadWaiveAsked,
} from '@/components/Event/NewScript/utils/errata'
import { CrTurnaroundSelection, EXPEDITED_PENDING } from '@/constants'
import { Event } from '@/types'
import { shouldSaTakeTranscriptOrders } from '@/utils/parrot'
import { getEventTimezone } from '@/utils/time'

// The CNA fields are rendered by EventResultFields (a shared component that can't own
// step registration), so register their paths with the enclosing step here — otherwise
// the Closing step's status circle wouldn't reflect a missing cna_type / cna_declared_at.
// Must render inside the Step so useRegisterStepFields sees the step's registry context.
const RegisterStepFields: React.FC<{ paths: string[] }> = ({ paths }) => {
  useRegisterStepFields(paths)
  return null
}

// Closing section (per witness). Result / read-waive / orders / CNA all bind
// to events_feedbacks[witnessIndex].
export const ClosingStep: React.FC<{
  event: Event
  form: CrFormHandle
  witnessIndex: number
  isLast?: boolean
}> = ({ event, form, witnessIndex, isLast }) => {
  const dt = useFormattedCurrentDateParts(getEventTimezone(event))
  const prefix = `events_feedbacks.${witnessIndex}`

  const witnessFeedback = form.values.events_feedbacks?.[witnessIndex]
  const attendees = witnessFeedback?.attendees ?? []
  const attendeeOrders = witnessFeedback?.attendee_orders ?? {}
  const readWaive = witnessFeedback?.read_waive ?? null
  const announceRecord = form.values.sa?.announce_record ?? null
  const stateScript = getStateRecordScript(event)
  const takesOrders = shouldSaTakeTranscriptOrders(event)
  const ordersPresent = witnessFeedback?.orders_present ?? null

  // Orders error (no order / pending expedited) surfaces after a validation attempt and is
  // recomputed live so it clears once the orders are filled in.
  const orderValues = Object.values(attendeeOrders)
  const ordersIncomplete =
    ordersPresent === true &&
    (orderValues.some(value => value === EXPEDITED_PENDING) ||
      !orderValues.some(
        value => value !== null && value !== '' && value !== EXPEDITED_PENDING,
      ))
  const ordersError =
    ordersIncomplete && form.errors[`${prefix}.orders_complete`]

  // Register step fields whose inputs can't own registration: CNA fields (rendered by the
  // shared EventResultFields; required when result === 'canceled_cna') and orders_complete
  // (the pseudo field that blocks an incomplete expedited order). Registering them lets the
  // step's status circle turn invalid/done based on their state.
  const stepFieldPaths = useMemo(
    () => [
      `${prefix}.read_waive`,
      `${prefix}.orders_present`,
      `${prefix}.cna_type`,
      `${prefix}.cna_type_other_text`,
      `${prefix}.cna_declared_at`,
      `${prefix}.orders_complete`,
    ],
    [prefix],
  )

  // Keeps the recorded answer in step with the rule: "did not apply" where the
  // reporter is not asked, which reaches parrot as "no answer" for it to
  // resolve, and replaces anything a draft carried from before the rule applied.
  useEffect(() => {
    const answer = crReadWaiveToRecord(event.errata_policy, readWaive)
    if (answer !== readWaive) {
      form.setFieldValue(`${prefix}.read_waive`, answer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.errata_policy, readWaive, prefix])

  const setAttendeeOrder = (attendeeId: string, value: CrTurnaroundSelection) =>
    form.setFieldValue(`${prefix}.attendee_orders`, {
      ...attendeeOrders,
      [attendeeId]: value,
    })

  return (
    <Step
      id={witnessStepKey(CrFormStep.CLOSING, witnessIndex)}
      title="Closing"
      form={form}
      isLast={isLast}
    >
      <Stack>
        <RegisterStepFields paths={stepFieldPaths} />
        <Alert color="yellow" title="Reminders">
          {isReadWaiveAsked(event.errata_policy)
            ? 'If the Court Reporter has not, ask the witness about read/waive.'
            : 'Read/waive does not apply to this event, so there is nothing to ask.'}
          {takesOrders && ' Confirm whether there are any transcript orders.'}
        </Alert>

        {isReadWaiveAsked(event.errata_policy) ? (
          <>
            <ScriptBlock heading="Transcript information">
              If a transcript is ordered, will the witness read and sign the
              transcript, or waive signature?
            </ScriptBlock>

            <Select
              required
              label="Read / waive"
              placeholder="Select one"
              data={READ_WAIVE_OPTIONS}
              {...form.getInputProps(`${prefix}.read_waive`)}
            />
          </>
        ) : (
          <Group spacing="xs">
            <Text size="sm" fw={500}>
              Read / waive
            </Text>
            <Text size="sm">
              {fixedReadWaiveAnswer(event.errata_policy) === 'read'
                ? 'Read \u2014 this jurisdiction always reads'
                : 'Waive \u2014 this jurisdiction never reads'}
            </Text>
          </Group>
        )}
        {takesOrders && (
          <BooleanSelect
            required
            label="Any orders at this time?"
            placeholder="Select one"
            value={ordersPresent}
            error={form.errors[`${prefix}.orders_present`]}
            onChange={value => {
              form.setFieldValue(`${prefix}.orders_present`, value)
              // Drop any per-attendee turnaround selections when orders are no longer
              // present, so stale entries aren't submitted as real orders.
              if (value !== true) {
                form.setFieldValue(`${prefix}.attendee_orders`, {})
              }
            }}
          />
        )}

        {takesOrders && ordersPresent === true && (
          <Stack spacing="xs">
            <Text size="sm" fw={600}>
              Transcript orders
            </Text>
            {attendees.map((attendee, index) => {
              const attendeeId = attendee.id ?? String(index)
              return (
                <CrTurnaroundSelect
                  key={attendeeId}
                  label={attendee.name || `Attendee ${index + 1}`}
                  value={
                    (attendeeOrders[attendeeId] ??
                      null) as CrTurnaroundSelection
                  }
                  onChange={value => setAttendeeOrder(attendeeId, value)}
                />
              )
            })}
            {ordersError && (
              <Text size="sm" color="red" data-invalid="true">
                {ordersError}
              </Text>
            )}
          </Stack>
        )}

        {stateScript ? (
          <StateScriptBlock
            heading="Going off the record (closing)"
            lines={stateScript.closing}
            values={getScriptValues(event, null, dt)}
          />
        ) : (
          <ConditionalScriptBlock
            heading="Going off the record (closing)"
            announceRecord={announceRecord}
          >
            This concludes today&apos;s proceeding. We are going off the record.
            The time is {dt.formattedTimeWithTimezone}.
          </ConditionalScriptBlock>
        )}

        <TimestampsControl
          event={event}
          form={form}
          witnessIndex={witnessIndex}
        />

        <Textarea
          label="Spellings"
          description="Capture name and term spellings confirmed on the record."
          minRows={3}
          autosize
          {...form.getInputProps(`${prefix}.spellings`)}
          value={witnessFeedback?.spellings ?? ''}
        />
        <Textarea
          label="General notes"
          minRows={3}
          autosize
          {...form.getInputProps(`${prefix}.additional_notes`)}
          value={witnessFeedback?.additional_notes ?? ''}
        />

        <EventResultFields
          event={event}
          form={form}
          witnessIndex={witnessIndex}
        />

        <Alert color="indigo" title="Before you submit">
          Review all information in every section above before submitting the
          form.
        </Alert>
      </Stack>
    </Step>
  )
}
